'use client';

import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { quoteLevel, type AiPricing } from '@/lib/levels';

/**
 * How a price is said beside a level.
 *
 *   kharma — a signed-in person with KHARMA on: "≈ N per translation".
 *   free   — a guest: they translate with free credits, never KHARMA, so a
 *            KHARMA figure would be a price they are not paying.
 *   none   — KHARMA is switched off: there is no price to show at all.
 */
export type LevelPriceMode = 'kharma' | 'free' | 'none';

/**
 * The translation level, in the language bar beside the Overview switch.
 *
 * One small button — the level's name and about what this text costs at it —
 * that opens a list of all five with their blurbs and prices. A list rather
 * than a hover card so a phone gets the same thing as a mouse: a tap opens it.
 *
 * A listbox, keyboard and all: ↑/↓ (and Home/End) move, Enter or Space picks,
 * Escape closes and hands the focus back to the button, and moving the focus
 * out of it closes it.
 *
 * Guests translate at the guest level; the others are shown, so they can be
 * seen, and say "Sign in to use".
 *
 * The prices follow the text a moment behind the typing (they are estimates;
 * the answer says what it really cost).
 */
export default function LevelPicker({
  pricing,
  level,
  guest,
  text,
  onPick,
  price = guest ? 'free' : 'kharma',
  freeLeft = null,
}: {
  pricing: AiPricing;
  level: string;
  guest: boolean;
  text: string;
  onPick: (key: string) => void;
  price?: LevelPriceMode;
  /** A guest's free translations left, when known. */
  freeLeft?: number | null;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [priced, setPriced] = useState(text);
  const box = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const uid = useId();
  const listId = `${uid}-levels`;
  const optId = (i: number) => `${uid}-level-${i}`;

  useEffect(() => {
    const t = setTimeout(() => setPriced(text), 400);
    return () => clearTimeout(t);
  }, [text]);

  // Every level is shown with an overview riding along, because every answer
  // carries one (the switch only shows or hides it — see the translate call).
  const prices = useMemo(
    () => Object.fromEntries(pricing.levels.map((l) => [l.key, quoteLevel(pricing, l, priced, true)])),
    [pricing, priced],
  );

  const locked = (key: string) => guest && key !== pricing.guestLevel;
  const currentIndex = Math.max(0, pricing.levels.findIndex((l) => l.key === level));
  const current = pricing.levels[currentIndex] ?? pricing.levels[0];

  /** What the price slot says for one level. */
  const priceText = (key: string): string => {
    if (locked(key)) return 'Sign in to use';
    if (price === 'none') return '';
    if (price === 'free') {
      return freeLeft !== null && Number.isFinite(freeLeft)
        ? `${freeLeft} free left`
        : 'free translations';
    }
    return `≈ ${prices[key]} per translation`;
  };

  // A tap or click anywhere else closes it.
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent | TouchEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('touchstart', away);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('touchstart', away);
    };
  }, [open]);

  // Opening puts the focus on the list, on the level in use.
  useEffect(() => {
    if (!open) return;
    setActive(currentIndex);
    requestAnimationFrame(() => list.current?.focus());
    // Only on opening: `currentIndex` changing while open is the pick itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus) requestAnimationFrame(() => trigger.current?.focus());
  };

  const choose = (i: number) => {
    const l = pricing.levels[i];
    if (!l || locked(l.key)) return;
    onPick(l.key);
    close(true);
  };

  /** The next selectable option from `from` in direction `dir`, wrapping; `from` itself if none. */
  const step = (from: number, dir: 1 | -1): number => {
    const n = pricing.levels.length;
    for (let k = 1; k <= n; k++) {
      const i = (from + dir * k + n) % n;
      if (!locked(pricing.levels[i].key)) return i;
    }
    return from;
  };

  const onListKey = (e: KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); setActive((a) => step(a, 1)); break;
      case 'ArrowUp': e.preventDefault(); setActive((a) => step(a, -1)); break;
      case 'Home': e.preventDefault(); setActive(step(pricing.levels.length - 1, 1)); break;
      case 'End': e.preventDefault(); setActive(step(0, -1)); break;
      case 'Enter':
      case ' ': e.preventDefault(); choose(active); break;
      case 'Escape': e.preventDefault(); e.stopPropagation(); close(true); break;
      case 'Tab': setOpen(false); break;
      default: break;
    }
  };

  const onTriggerKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
    }
  };

  const triggerPrice = locked(current.key) || price === 'none'
    ? ''
    : price === 'free'
      ? 'free'
      : `≈${prices[current.key]}`;

  return (
    <div
      ref={box}
      className="relative"
      // Focus leaving the picker altogether closes it.
      onBlur={(e) => {
        if (open && box.current && !box.current.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <button
        ref={trigger}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={
          `Translation level: ${current.name}` +
          (price === 'kharma' ? `, about ${prices[current.key]} KHARMA per translation` : price === 'free' ? ', free translations' : '')
        }
        title={current.blurb}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onTriggerKey}
        className="flex h-9 shrink-0 items-center gap-1 rounded-lg border border-transparent px-2 text-sm
                   text-ink-900 transition hover:border-ink-200 hover:bg-ink-100 dark:text-ink-dim
                   dark:hover:border-ink-700 dark:hover:bg-ink-800"
      >
        <span className="font-medium">{current.name}</span>
        {triggerPrice && <span className="text-xs tabular-nums text-ink-400">{triggerPrice}</span>}
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 text-ink-400" fill="none" stroke="currentColor"
             strokeWidth="2" aria-hidden>
          <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute right-0 top-full z-40 mt-1.5 w-72 max-w-[calc(100vw-2rem)] rounded-lg border
                     border-ink-200 bg-white p-1 shadow-xl dark:border-ink-700 dark:bg-ink-800"
        >
          <p id={`${listId}-label`} className="px-2.5 pb-1 pt-1.5 text-xs text-ink-500 dark:text-ink-300">
            Translation level
            {price === 'kharma' && ' · about what one translation of this text costs'}
          </p>
          <div
            ref={list}
            id={listId}
            role="listbox"
            tabIndex={-1}
            aria-labelledby={`${listId}-label`}
            aria-activedescendant={optId(active)}
            onKeyDown={onListKey}
            className="outline-none"
          >
            {pricing.levels.map((l, i) => {
              const off = locked(l.key);
              const on = l.key === current.key;
              const focused = i === active;
              return (
                <div
                  key={l.key}
                  id={optId(i)}
                  role="option"
                  aria-selected={on}
                  aria-disabled={off || undefined}
                  onMouseMove={() => { if (!off && active !== i) setActive(i); }}
                  onClick={() => choose(i)}
                  className={
                    'block w-full cursor-pointer rounded-md px-2.5 py-2 text-left transition ' +
                    (off
                      ? 'cursor-not-allowed opacity-60'
                      : on
                        ? 'bg-accent-50 dark:bg-accent-500/15'
                        : focused
                          ? 'bg-ink-100 dark:bg-ink-700'
                          : 'hover:bg-ink-100 dark:hover:bg-ink-700') +
                    (focused && !off ? ' ring-2 ring-inset ring-accent-400' : '')
                  }
                >
                  <span className="flex items-baseline justify-between gap-3">
                    <span className={'text-sm font-semibold ' + (on ? 'text-accent-700 dark:text-accent-300' : 'text-ink-900 dark:text-ink-50')}>
                      {l.name}
                    </span>
                    <span className="shrink-0 text-xs tabular-nums text-ink-500 dark:text-ink-300">{priceText(l.key)}</span>
                  </span>
                  <span className="mt-0.5 block text-xs leading-snug text-ink-500 dark:text-ink-300">{l.blurb}</span>
                </div>
              );
            })}
          </div>
          {price === 'kharma' && (
            <p className="px-2.5 pb-1.5 pt-1 text-[11px] leading-snug text-ink-400 dark:text-ink-400">
              Above {pricing.levels[0]?.name ?? 'the first level'}, nothing is sent while you type: press
              Translate (or Ctrl+Enter) for one translation. Up to about twice the estimate is held while
              it is written; you pay what it really uses.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
